(() => {
  const EVT_PREFIX = 'hira-product-form:';
  const SUCCESS_MS = 2000;

  // Entity decoding, NOT currency formatting: the shop's money format may
  // contain HTML entities for its currency symbol. Liquid renders those as
  // glyphs in the SSR paint while the JSON payload carries them literally, so
  // decoding once here is what makes both strings identical. No currency maths
  // and no formatting happens in this file — every value it writes is
  // pre-formatted by Liquid.
  let decoder = null;
  const decode = (value) => {
    if (value == null) return '';

    decoder ??= document.createElement('textarea');
    // Safe: a textarea's content is parsed as text and never executed.
    decoder.innerHTML = String(value);
    return decoder.value;
  };

  class HiraPriceAtc extends HTMLElement {
    connectedCallback() {
      this.priceEl = this.querySelector('[data-hira-price]');
      this.compareEl = this.querySelector('[data-hira-compare]');
      this.discountEl = this.querySelector('[data-hira-discount]');
      this.savingsEl = this.querySelector('[data-hira-savings]');
      this.stepper = this.querySelector('[data-hira-stepper]');
      this.qtyDisplay = this.querySelector('[data-hira-qty-display]');
      this.qtySr = this.querySelector('[data-hira-qty-sr]');
      this.decrementBtn = this.querySelector('[data-hira-qty-step="-1"]');
      this.incrementBtn = this.querySelector('[data-hira-qty-step="1"]');
      this.atcWrap = this.querySelector('.cartx-product-button-wrap');
      this.atcBtn = this.querySelector('.cartx-add-to-cart');
      this.atcLabelEl = this.querySelector('[data-hira-atc-label]');
      this.statusEl = this.querySelector('[data-hira-atc-status]');
      this.successTimer = null;

      // The controller emits every `hira-product-form:*` event on the
      // <hira-product-form> ancestor, so the listeners must live there —
      // listening on `this` would never fire.
      this.host = this.closest('hira-product-form');

      this.max = Number(this.stepper?.dataset.hiraQtyMax) || Infinity;

      const { saveTemplate, taxNote, atcLabel, soldOutLabel, successMessage, errorMessage } =
        this.dataset;
      this.saveTemplate = saveTemplate ?? '';
      this.taxNote = taxNote ?? '';
      this.atcLabel = atcLabel ?? '';
      this.soldOutLabel = soldOutLabel ?? '';
      this.successMessage = successMessage ?? '';
      this.errorMessage = errorMessage ?? '';

      this.available = !this.atcBtn?.classList.contains('cartx-sold-out');

      this.onStepperClick = this.onStepperClick.bind(this);
      this.onVariantChanged = this.onVariantChanged.bind(this);
      this.onQtyChanged = this.onQtyChanged.bind(this);
      this.onAddToCart = this.onAddToCart.bind(this);
      this.onAddSuccess = this.onAddSuccess.bind(this);
      this.onAddError = this.onAddError.bind(this);

      this.addEventListener('click', this.onStepperClick);
      this.host?.addEventListener(`${EVT_PREFIX}variant-changed`, this.onVariantChanged);
      this.host?.addEventListener(`${EVT_PREFIX}qty-changed`, this.onQtyChanged);
      this.host?.addEventListener(`${EVT_PREFIX}add-to-cart`, this.onAddToCart);
      this.host?.addEventListener(`${EVT_PREFIX}add-to-cart-success`, this.onAddSuccess);
      this.host?.addEventListener(`${EVT_PREFIX}add-to-cart-error`, this.onAddError);

      // The controller owns the quantity, so read it rather than assuming 1 — a
      // theme-editor re-render of this block alone must not drift from state.
      this.renderQuantity(this.host?.getState?.().quantity ?? 1);
    }

    disconnectedCallback() {
      clearTimeout(this.successTimer);
      this.successTimer = null;
      this.removeEventListener('click', this.onStepperClick);
      this.host?.removeEventListener(`${EVT_PREFIX}variant-changed`, this.onVariantChanged);
      this.host?.removeEventListener(`${EVT_PREFIX}qty-changed`, this.onQtyChanged);
      this.host?.removeEventListener(`${EVT_PREFIX}add-to-cart`, this.onAddToCart);
      this.host?.removeEventListener(`${EVT_PREFIX}add-to-cart-success`, this.onAddSuccess);
      this.host?.removeEventListener(`${EVT_PREFIX}add-to-cart-error`, this.onAddError);
    }

    onStepperClick(event) {
      const button = event.target.closest?.('[data-hira-qty-step]');
      const step = Number(button?.dataset.hiraQtyStep);
      if (!Number.isFinite(step)) return;

      if (typeof this.host?.setQuantity !== 'function') return;

      const current = this.host.getState().quantity;
      this.host.setQuantity(current + step);

      // Never trust the local arithmetic: setQuantity() clamps to the tracked
      // inventory and returns silently when the clamped value is unchanged, so
      // the display is always rendered from the state it actually reached.
      this.renderQuantity(this.host.getState().quantity);
    }

    onQtyChanged(event) {
      this.renderQuantity(event.detail?.quantity ?? 1);
    }

    renderQuantity(value) {
      if (this.qtyDisplay) this.qtyDisplay.textContent = String(value).padStart(2, '0');
      if (this.qtySr) this.qtySr.textContent = String(value);
      if (this.decrementBtn) this.decrementBtn.disabled = value <= 1;
      if (this.incrementBtn) this.incrementBtn.disabled = value >= this.max;
    }

    onVariantChanged(event) {
      const { variant, quantity, available } = event.detail ?? {};
      if (!variant) return;

      if (this.priceEl) this.priceEl.textContent = decode(variant.price_formatted);

      const hasCompare = variant.compare_at_formatted != null;

      if (this.compareEl) {
        this.compareEl.textContent = decode(variant.compare_at_formatted);
        this.compareEl.hidden = !hasCompare;
      }

      if (this.savingsEl) {
        this.savingsEl.textContent = this.renderSaveLine(variant.savings_formatted);
        this.savingsEl.hidden = !hasCompare;
      }

      if (this.discountEl) {
        this.discountEl.textContent = `${variant.discount_percent}% OFF`;
        this.discountEl.hidden = !hasCompare || variant.discount_percent === 0;
      }

      this.max =
        variant.inventory_policy !== 'continue' && variant.inventory_quantity > 0
          ? variant.inventory_quantity
          : Infinity;
      this.renderQuantity(quantity ?? this.host?.getState?.().quantity ?? 1);

      this.setAvailability(available);

      // The wrapper's data-variant-id / data-price are deliberately left alone:
      // the controller's syncCartxWrapper() runs inside setVariant() before this
      // event is emitted, so they are already current.
    }

    setAvailability(available) {
      const changed = this.available !== available;
      this.available = available;

      if (this.atcBtn) {
        this.atcBtn.disabled = !available;
        this.atcBtn.classList.toggle('cartx-sold-out', !available);
      }
      if (this.atcLabelEl) {
        this.atcLabelEl.textContent = available ? this.atcLabel : this.soldOutLabel;
      }
      this.atcWrap?.classList.toggle('is-sold-out', !available);

      if (!changed || !this.atcWrap) return;

      // CartX binds click handlers by selector at load time and only re-runs its
      // binding pass when a node carrying `.cartx-product-button-wrap` is ADDED
      // to the DOM, so a variant that becomes buyable would otherwise have no
      // handler. Re-inserting the wrapper produces exactly that mutation; the
      // binding pass removes before it adds, so this can never double-bind. Only
      // done when availability actually changed, since re-insertion drops focus.
      this.atcWrap.replaceWith(this.atcWrap);
    }

    onAddToCart() {
      clearTimeout(this.successTimer);
      this.successTimer = null;
      this.setStatus('');
      this.atcBtn?.classList.remove('is-success');
      // CartX's own class — it reveals the `.cartx-atc-loading` spinner already
      // in the markup, so no spinner CSS is authored here. CartX adds the same
      // class on its click path; adding it twice is a no-op.
      this.atcWrap?.classList.add('cartx-button-loadding');
    }

    onAddSuccess() {
      this.atcWrap?.classList.remove('cartx-button-loadding');
      this.setStatus(this.successMessage);
      this.atcBtn?.classList.add('is-success');

      clearTimeout(this.successTimer);
      this.successTimer = setTimeout(() => {
        this.setStatus('');
        this.atcBtn?.classList.remove('is-success');
        this.successTimer = null;
      }, SUCCESS_MS);

      // Deliberately does NOT dispatch `cart:updated` — the controller's
      // handleCartMutated() already did, and a second dispatch would
      // double-bump the header cart count.
    }

    onAddError() {
      this.atcWrap?.classList.remove('cartx-button-loadding');
      this.atcBtn?.classList.remove('is-success');
      this.setStatus(this.errorMessage, true);
    }

    setStatus(message, isError = false) {
      if (!this.statusEl) return;

      this.statusEl.textContent = message;
      this.statusEl.classList.toggle('is-error', isError);
    }

    // `[amount]` and `[tax_note]` are literal placeholders inside a theme
    // setting. Square brackets rather than `{{ }}` because the theme editor
    // reads double braces in a text setting as a dynamic-source binding.
    renderSaveLine(amountFormatted) {
      return this.saveTemplate
        .replace('[amount]', decode(amountFormatted))
        .replace('[tax_note]', this.taxNote);
    }
  }

  if (!customElements.get('hira-price-atc')) {
    customElements.define('hira-price-atc', HiraPriceAtc);
  }
})();
